import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api/client.js";
import {
  compressImageForUpload,
  MAX_UPLOAD_BYTES,
  UPLOAD_IMAGE_MIMES,
  type UploadImageMime,
} from "../util/imageCompression.js";

/** RFQ API contract — kept in lockstep with /customer/api/src/domain/rfq.ts. */
export type ServiceCategory =
  | "plumbing"
  | "electrical"
  | "hvac"
  | "roofing"
  | "landscaping"
  | "painting"
  | "cleaning"
  | "appliance_repair"
  | "general_handyman"
  | "pest_control"
  | "other";

export const serviceCategoryLabels: Record<ServiceCategory, string> = {
  plumbing: "Plumbing",
  electrical: "Electrical",
  hvac: "HVAC",
  roofing: "Roofing",
  landscaping: "Landscaping",
  painting: "Painting",
  cleaning: "Cleaning",
  appliance_repair: "Appliance repair",
  general_handyman: "General handyman",
  pest_control: "Pest control",
  other: "Other",
};

export const serviceCategoryOptions: ServiceCategory[] = [
  "plumbing",
  "electrical",
  "hvac",
  "roofing",
  "landscaping",
  "painting",
  "cleaning",
  "appliance_repair",
  "general_handyman",
  "pest_control",
  "other",
];

export type Urgency = "emergency" | "urgent" | "normal" | "flexible";

export const urgencyLabels: Record<Urgency, string> = {
  emergency: "Emergency · within 24h",
  urgent: "Urgent · within a few days",
  normal: "Normal · within ~2 weeks",
  flexible: "Flexible · whenever",
};

export const urgencyOptions: Urgency[] = ["emergency", "urgent", "normal", "flexible"];

export type RfqStatus = "SUBMITTED" | "IN_REVIEW" | "CLOSED" | "CANCELLED";

export interface RfqLocation {
  street: string;
  unit?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export type AllowedImageMime = UploadImageMime;

export const allowedImageMimes: readonly AllowedImageMime[] = UPLOAD_IMAGE_MIMES;

export const MAX_IMAGE_BYTES = MAX_UPLOAD_BYTES;

export interface RfqImage {
  id: string;
  key: string;
  contentType: AllowedImageMime;
  sizeBytes: number;
  uploadedAt: string;
}

export interface RfqQuestion {
  questionId: string;
  question: string;
  askedAt: string;
  answer?: string;
  answeredAt?: string;
}

export interface AttachedBinderMedia {
  mediaId: string;
  contentType: AllowedImageMime;
}

/** Snapshot of a Property File entry the customer shared with this request. */
export interface AttachedBinderEntry {
  entryId: string;
  title: string;
  entryType: string;
  locationLabel?: string;
  notes?: string;
  facts?: Record<string, { label: string; value: string | number | boolean; valueType: string; unit?: string }>;
  dates?: Record<string, { value: string; precision: string; estimated?: boolean }>;
  media: AttachedBinderMedia[];
}

export interface Rfq {
  id: string;
  customerSub: string;
  propertyId: string;
  serviceCategory: ServiceCategory;
  urgency: Urgency;
  description: string;
  location: RfqLocation;
  status: RfqStatus;
  images?: RfqImage[];
  /** Property File entries shared with this request (Slice 4). */
  attachedBinder?: AttachedBinderEntry[];
  /** Pre-bid contractor Q&A — answers are shared with all invited contractors. */
  questions?: RfqQuestion[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateRfqInput {
  propertyId: string;
  serviceCategory: ServiceCategory;
  urgency: Urgency;
  description: string;
  /**
   * Number of photos the client will upload immediately after creating the
   * RFQ. The server uses this to defer the ops notification email until the
   * expected images have been attached, so the email actually contains them.
   * Omit (or 0) when no photos will be uploaded.
   */
  expectedImageCount?: number;
  /** Property File entry ids to snapshot onto this request (Slice 4). */
  binderEntryIds?: string[];
  /**
   * Share the property's auto-detected insights (lot size, home sqft, lawn
   * area) with invited contractors. Only honored for the categories in
   * INSIGHTS_SHARE_CATEGORIES (see rfqs/steps/ShareInsightsField.tsx) —
   * omit the field entirely for every other category.
   */
  sharePropertyInsights?: boolean;
}

export interface UpdateRfqInput {
  serviceCategory?: ServiceCategory;
  urgency?: Urgency;
  description?: string;
}

const QK = {
  list: ["rfqs"] as const,
  detail: (id: string) => ["rfqs", id] as const,
};

export function useRfqsList(opts: { status?: RfqStatus } = {}) {
  const { status } = opts;
  return useQuery({
    queryKey: status ? (["rfqs", { status }] as const) : QK.list,
    queryFn: async () => {
      const path = status ? `/rfqs?status=${encodeURIComponent(status)}` : "/rfqs";
      const res = await apiFetch<{ items: Rfq[] }>(path);
      return res.items;
    },
  });
}

export function useRfq(id: string | undefined) {
  return useQuery({
    queryKey: id ? QK.detail(id) : ["rfqs", "noop"],
    queryFn: () => apiFetch<Rfq>(`/rfqs/${id!}`),
    enabled: !!id,
  });
}

export function useCreateRfq() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRfqInput) => apiFetch<Rfq>("/rfqs", { method: "POST", json: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

// ---- AI service classification (anonymous intake) -------------------------

export interface ClassifyGuess {
  category: ServiceCategory;
  confidence: number;
}

/** A confident-enough mapping of free text to a service category. */
export interface ClassifyResult {
  category: ServiceCategory;
  confidence: number;
  /** Next-best categories (most-likely first), used when confidence is low. */
  alternates: ClassifyGuess[];
}

/** The server tells us to fall back to manual selection. */
export type ClassifyResponse = ClassifyResult | { fallback: "manual" };

/**
 * Classify a free-text issue description into a service category via the public
 * intake Lambda. Anonymous (runs before sign-up). Pass an AbortSignal to
 * enforce the client-side latency budget; on timeout/error the caller falls
 * back to manual category selection. The endpoint always returns 200 — it never
 * throws on status — but may throw on abort/network, which the caller handles.
 */
export function classifyService(text: string, signal?: AbortSignal): Promise<ClassifyResponse> {
  return apiFetch<ClassifyResponse>("/public/intake/classify", {
    method: "POST",
    json: { text },
    signal,
  });
}

// ---- Images ---------------------------------------------------------------

export interface PresignUploadResponse {
  imageId: string;
  key: string;
  contentType: AllowedImageMime;
  uploadUrl: string;
  expiresInSeconds: number;
}

export interface PresignDownloadResponse {
  imageId: string;
  contentType: AllowedImageMime;
  sizeBytes: number;
  url: string;
  expiresInSeconds: number;
}

/**
 * Presign + upload + attach an image. Bundled into one helper because the
 * three steps must happen in order against the same RFQ id.
 *
 * Compression runs FIRST: it can change the content type (HEIC → JPEG), and
 * the server binds Content-Type into the presigned PUT, so the presign has to
 * be asked for whatever we're actually going to send.
 */
export async function uploadRfqImage(args: {
  rfqId: string;
  file: File;
  contentType: AllowedImageMime;
  onProgress?: (loaded: number, total: number) => void;
}): Promise<Rfq> {
  const { file, contentType } = await compressImageForUpload(args.file, args.contentType);

  const presign = await apiFetch<PresignUploadResponse>(`/rfqs/${args.rfqId}/images/presign`, {
    method: "POST",
    json: { contentType },
  });

  // Use XHR for upload progress; fetch doesn't expose it for request bodies.
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", presign.uploadUrl);
    // From the presign response, not our local copy — the PUT header must match
    // the signed one exactly or S3 rejects it.
    xhr.setRequestHeader("Content-Type", presign.contentType);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) args.onProgress?.(ev.loaded, ev.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 upload failed: HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error("S3 upload network error"));
    xhr.send(file);
  });

  return apiFetch<Rfq>(`/rfqs/${args.rfqId}/images`, {
    method: "POST",
    json: {
      imageId: presign.imageId,
      key: presign.key,
      contentType: presign.contentType,
      sizeBytes: file.size,
    },
  });
}

export function useRfqImageUrl(rfqId: string, imageId: string | undefined) {
  return useQuery({
    queryKey: imageId
      ? (["rfqs", rfqId, "images", imageId, "url"] as const)
      : ["rfqs", rfqId, "images", "noop"],
    queryFn: () => apiFetch<PresignDownloadResponse>(`/rfqs/${rfqId}/images/${imageId!}/url`),
    enabled: !!imageId,
    // Presigned GET URLs expire in 5 minutes; refetch a bit before then.
    staleTime: 4 * 60 * 1000,
    gcTime: 4 * 60 * 1000,
  });
}

export function useDeleteRfqImage(rfqId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (imageId: string) =>
      apiFetch<Rfq>(`/rfqs/${rfqId}/images/${imageId}`, { method: "DELETE" }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.detail(rfqId), updated);
      void qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

export function useUpdateRfq(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateRfqInput) =>
      apiFetch<Rfq>(`/rfqs/${id}`, { method: "PATCH", json: input }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.detail(id), updated);
      void qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

export function useCancelRfq() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<Rfq>(`/rfqs/${id}/cancel`, { method: "POST" }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.detail(updated.id), updated);
      void qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

export function useAnswerQuestion(rfqId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { questionId: string; answer: string }) =>
      apiFetch<Rfq>(`/rfqs/${rfqId}/questions/${args.questionId}/answer`, {
        method: "POST",
        json: { answer: args.answer },
      }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.detail(updated.id), updated);
    },
  });
}

export interface QuoteMoney {
  currency: "USD";
  amountCents: number;
}

export interface QuoteProjection {
  rfqId: string;
  quoteId: string;
  /** Opaque per-invitation thread handle (unit 13). */
  invitationId?: string;
  status: "ACTIVE" | "WITHDRAWN";
  quote?: {
    type: "fixed" | "range" | "needs_visit";
    amount?: QuoteMoney;
    minAmount?: QuoteMoney;
    maxAmount?: QuoteMoney;
    notes?: string;
    availabilityWindows?: { date: string; note?: string }[];
  };
  contractorProfile?: { businessName: string; yearsExperience: number; about?: string };
  submittedAt?: string;
  updatedAt: string;
}

export interface QuoteSelection {
  rfqId: string;
  quoteId: string;
  status: "SELECTED" | "VOIDED";
  selectedAt: string;
  voidedReason?: string;
}

export type JobStatus =
  | "SELECTED"
  | "ACCEPTED"
  | "SCHEDULED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "CANCELLED";

/** Post-selection job status projection (unit 18) — drives the timeline. */
export interface JobProjection {
  rfqId: string;
  jobId: string;
  quoteId: string;
  status: JobStatus;
  scheduledFor?: string;
  statusTimestamps: Partial<Record<JobStatus, string>>;
  updatedAt: string;
}

/** Mirrors MAX_FEEDBACK_COMMENT in customer/api/src/domain/feedback.ts. */
export const MAX_FEEDBACK_COMMENT = 1000;

/** The customer's own rating of the pro who did the work. Staff-only downstream. */
export interface CustomerFeedback {
  rfqId: string;
  quoteId: string;
  workCompleted: boolean;
  rating?: number;
  comment?: string;
  source: "customer" | "staff";
  submittedAt: string;
}

export interface RfqQuotesResponse {
  quotes: QuoteProjection[];
  selection: QuoteSelection | null;
  jobs: JobProjection[];
  feedback: CustomerFeedback | null;
}

export function useRfqQuotes(rfqId: string | undefined) {
  return useQuery({
    queryKey: ["rfqs", rfqId, "quotes"],
    enabled: Boolean(rfqId),
    queryFn: () => apiFetch<RfqQuotesResponse>(`/rfqs/${rfqId!}/quotes`),
    refetchInterval: 30_000,
  });
}

export function useSelectQuote(rfqId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (quoteId: string) =>
      apiFetch<{ selection: QuoteSelection }>(`/rfqs/${rfqId}/quotes/${quoteId}/select`, {
        method: "POST",
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["rfqs", rfqId, "quotes"] }),
  });
}

/**
 * Submit (or correct) the customer's feedback. Re-submitting overwrites, which
 * is what makes the one-tap star links in the email safe: a mis-tap lands on
 * the form pre-filled, and changing it before sending costs nothing.
 */
export function useSubmitFeedback(rfqId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { workCompleted: boolean; rating?: number; comment?: string }) =>
      apiFetch<{ feedback: CustomerFeedback }>(`/rfqs/${rfqId}/feedback`, {
        method: "POST",
        json: input,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["rfqs", rfqId, "quotes"] }),
  });
}

export function formatQuotePrice(quote: QuoteProjection["quote"]): string {
  if (!quote) return "Quote withdrawn";
  const dollars = (cents: number) =>
    (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
  if (quote.type === "fixed" && quote.amount) return dollars(quote.amount.amountCents);
  if (quote.type === "range" && quote.minAmount && quote.maxAmount)
    return `${dollars(quote.minAmount.amountCents)} – ${dollars(quote.maxAmount.amountCents)}`;
  return "Wants to see the job before pricing";
}

export interface RfqThreadMessage {
  messageId: string;
  sender: "customer" | "contractor";
  body: string;
  sentAt: string;
}

export interface RfqThread {
  invitationId: string;
  businessName: string | null;
  open: boolean;
  messages: RfqThreadMessage[];
}

export function useRfqThreads(rfqId: string | undefined) {
  return useQuery({
    queryKey: ["rfqs", rfqId, "messages"],
    enabled: Boolean(rfqId),
    refetchInterval: 30_000,
    queryFn: () => apiFetch<{ threads: RfqThread[] }>(`/rfqs/${rfqId!}/messages`),
  });
}

export function useSendThreadMessage(rfqId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { invitationId: string; body: string }) =>
      apiFetch<RfqThreadMessage>(`/rfqs/${rfqId}/invitations/${args.invitationId}/messages`, {
        method: "POST",
        json: { body: args.body },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["rfqs", rfqId, "messages"] }),
  });
}
